import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";

/**
 * Recommended fixes: each agent holding roles or services it never uses, named in full.
 *
 * This is advice read from the agent's current profile, not a decision and not an action, so the
 * panel writes nothing and offers no button; each row links to the existing risk, where the work
 * is tracked. The list can differ from the risk's own wording, which records what was true when
 * the risk was raised. The date is labelled "Profile updated" rather than "measured" because a
 * profile can be refreshed without the list being measured again.
 */

// en-GB gives "1 Nov 2026". UTC because the server sends a UTC time and a local render could show
// the neighbouring day.
const formatProfileDate = (value) => new Date(value).toLocaleDateString("en-GB", {
  day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
});

const entryLabel = (entryKind) => (entryKind === "Role" ? "unused roles" : "unused services");

export default function AiRecommendedFixesPanel({ onCountChange, onUnavailable, refreshKey }) {
  const apiCall = useApi();
  const [items, setItems] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (isStale) => {
    setLoading(true);
    try {
      const data = await apiCall("GET", "/ai-assets/recommended-fixes");
      if (isStale()) return;
      if (!Array.isArray(data?.items)) throw new Error("malformed recommended fixes response");
      setItems(data.items);
      setUnavailable(false);
      onUnavailable?.(false);
      onCountChange?.(data.items.length);
    } catch {
      if (isStale()) return;
      setItems([]);
      setUnavailable(true);
      onUnavailable?.(true);
      // null, not 0: zero would feed "Nothing waiting", which is exactly what we do not know.
      onCountChange?.(null);
    } finally {
      if (!isStale()) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-read when a scan completes: that is what rewrites the profiles this list comes from.
  useEffect(() => {
    let stale = false;
    load(() => stale);
    return () => { stale = true; };
  }, [load, refreshKey]);

  if (loading) return null;

  if (unavailable) {
    return (
      <p className="text-sm text-content-secondary" data-testid="fixes-unavailable">
        Recommended fixes are unavailable.
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-content-secondary">
        No agents have unused roles or services we can recommend removing.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border">
      {items.map((item) => (
        <li key={item.aiAssetId} className="py-2 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <Link
              to={`/risks?riskId=${item.unifiedRiskId}`}
              className="text-sm font-medium text-content-primary hover:underline"
            >
              {item.assetName}
            </Link>
            <span className="text-[11px] text-content-secondary">
              Profile updated {formatProfileDate(item.measuredAt)}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-content-secondary break-words">
            {entryLabel(item.entryKind)}: {item.entries.join(", ")}
          </p>
        </li>
      ))}
    </ul>
  );
}
